
import React from 'react';
import { Icons } from '../components/ui/Icons.tsx';

interface PrivacyPolicyPageProps {
  onClose?: () => void;
}

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onClose }) => {
  return (
    <div className="w-full h-full flex flex-col bg-white animate-in fade-in duration-500 overflow-y-auto no-scrollbar">
      {/* Navigation / Header Area */}
      <div className="flex items-center justify-between px-10 py-8 sticky top-0 bg-white/80 backdrop-blur-md z-10 border-b border-gray-100">
        <div className="flex items-center gap-2">
           <Icons.Logo className="w-8 h-8" />
           <span className="text-xl font-bold tracking-tight">Neura AI</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all"
          >
            <Icons.X className="w-6 h-6" />
          </button>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-10 py-16 w-full">
        <div className="mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-4 tracking-tight">Privacy Policy</h1>
          <p className="text-lg text-gray-500 uppercase tracking-widest font-semibold">Last Updated: March 30, 2026</p>
        </div>

        <div className="prose prose-gray max-w-none">
          <p className="text-xl text-gray-600 leading-relaxed mb-12">
            Neura AI ("Neura", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our AI automation platform (the "Service").
          </p>

          <div className="space-y-12">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Information We Collect</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 shadow-sm transition-hover hover:shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">a. Personal Information</h3>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    <li>Name, email address, phone number</li>
                    <li>Account login details</li>
                    <li>Billing and payment information</li>
                  </ul>
                </div>
                <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 shadow-sm transition-hover hover:shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">b. Usage Data</h3>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    <li>IP address, device info, browser type</li>
                    <li>Interaction data (features used, actions performed)</li>
                    <li>Logs and timestamps</li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 p-6 bg-black text-white rounded-2xl shadow-xl">
                 <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Icons.Apps className="w-5 h-5" /> c. Connected App Data
                 </h3>
                 <p className="text-gray-300 mb-4 text-sm leading-relaxed">When you connect third-party apps (e.g., Gmail, Notion, Slack), we may access limited data required to perform tasks, such as:</p>
                 <ul className="list-disc pl-5 space-y-2 text-gray-300 text-sm">
                    <li>Email metadata (subject, sender)</li>
                    <li>Calendar events</li>
                    <li>Files and notes</li>
                 </ul>
                 <p className="mt-4 text-xs font-semibold text-gray-500 uppercase tracking-tighter">We only access data you explicitly authorize.</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Collect Data</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 font-medium">
                <li>Directly from you (account creation, usage)</li>
                <li>From integrations (via APIs)</li>
                <li>Automatically (cookies, analytics tools)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Data</h2>
              <p className="text-gray-600 mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Provide and operate the Service</li>
                <li>Execute tasks via AI agents</li>
                <li>Improve product performance and accuracy</li>
                <li>Communicate updates and support</li>
                <li>Prevent fraud and ensure security</li>
              </ul>
              <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-sm text-yellow-800">
                We may use aggregated, anonymized data for research and improvement.
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. AI & Automation Transparency</h2>
              <p className="text-gray-600 leading-relaxed font-medium">Neura uses AI to process inputs and perform actions.</p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-600">
                <li>Your data may be processed to generate outputs</li>
                <li>AI results may not always be accurate</li>
                <li>You are responsible for reviewing outputs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing</h2>
              <p className="text-xl font-bold text-gray-900 mb-4">We do <span className="underline decoration-yellow-400 decoration-wavy">not sell your data</span>.</p>
              <p className="text-gray-600 mb-4">We may share data with:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Service providers (hosting, payments, analytics)</li>
                <li>Integrated apps (only to execute your commands)</li>
                <li>Legal authorities (if required by law)</li>
              </ul>
              <p className="mt-4 text-sm font-medium text-gray-500 italic">All partners are required to protect your data.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Data Security</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <div className="p-5 border border-gray-100 rounded-2xl bg-white shadow-sm">
                    <div className="font-bold mb-1 text-black text-sm uppercase flex items-center gap-2">
                       <Icons.Settings className="w-4 h-4" /> Encryption
                    </div>
                    <div className="text-xs text-gray-500">Industry standard encryption for data in transit and at rest.</div>
                 </div>
                 <div className="p-5 border border-gray-100 rounded-2xl bg-white shadow-sm">
                    <div className="font-bold mb-1 text-black text-sm uppercase flex items-center gap-2">
                       <Icons.Shield className="w-4 h-4" /> Access Control
                    </div>
                    <div className="text-xs text-gray-500">Strict authentication and role-based access protocols.</div>
                 </div>
                 <div className="p-5 border border-gray-100 rounded-2xl bg-white shadow-sm">
                    <div className="font-bold mb-1 text-black text-sm uppercase flex items-center gap-2">
                       <Icons.History className="w-4 h-4" /> Monitoring
                    </div>
                    <div className="text-xs text-gray-500">24/7 monitoring for suspicious activity.</div>
                 </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                We retain your data while your account is active, as needed to provide services, and for legal and security purposes.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm font-bold text-gray-900">
                 You can request deletion anytime.
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
              <p className="text-gray-600 mb-4 font-medium">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Access your data</li>
                <li>Correct or delete your data</li>
                <li>Withdraw consent</li>
                <li>Request data portability</li>
              </ul>
              <p className="mt-4 text-sm text-gray-500 italic">To request this, contact us below.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Services</h2>
              <p className="text-gray-600 leading-relaxed">
                Neura integrates with third-party platforms. We are not responsible for their privacy practices. 
                You can revoke access anytime from your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Cookies & Tracking</h2>
              <p className="text-gray-600 mb-4 font-medium">We use cookies to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Improve user experience</li>
                <li>Analyze usage</li>
                <li>Maintain sessions</li>
              </ul>
              <p className="mt-4 text-sm text-gray-500">You can control cookies via browser settings.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Children’s Privacy</h2>
              <p className="text-gray-600 leading-relaxed font-semibold">
                 Neura is not intended for users under 13. We do not knowingly collect data from children.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Policy</h2>
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy. Continued use of Neura means you accept the changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <div className="p-8 bg-gray-50 rounded-3xl border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative">
                 <div className="absolute top-0 right-0 w-32 h-32 bg-black/5 rounded-bl-full transform translate-x-12 -translate-y-12"></div>
                 <div className="relative z-10">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Questions or data requests?</h4>
                    <p className="text-gray-500 text-sm">Our support team is here to help with your privacy concerns.</p>
                 </div>
                 <a href="mailto:useneuraai@gmail.com" className="relative z-10 px-8 py-3 bg-black text-white rounded-full font-bold hover:bg-gray-700 transition-all shadow-lg hover:shadow-black/20 active:scale-95">
                    useneuraai@gmail.com
                 </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
